import { type SetStateAction, useEffect } from "react";

import { create } from "zustand";
import { persist } from "zustand/middleware";

type MainSidebarState = {
  isCollapsed: boolean;
  isMounted: boolean;
};

type MainSidebarActions = {
  setCollapsed: (value: SetStateAction<boolean>) => void;
  setIsMounted: (value: boolean) => void;
};

type MainSidebarStore = ZustandStore<MainSidebarState, MainSidebarActions>;

const useMainSidebarStore = create<MainSidebarStore>()(
  persist(
    (set, get) => ({
      isCollapsed: false,
      isMounted: false,
      actions: {
        setCollapsed: (value) =>
          set({
            isCollapsed:
              typeof value === "function" ? value(get().isCollapsed) : value,
          }),
        setIsMounted: (value) => set({ isMounted: value }),
      },
    }),
    {
      name: "mainSideBarNavigationCollapsed",
      partialize: (state) => ({ collapsed: state.isCollapsed }),
    },
  ),
);

// This hook MUST be used only in the MainSideBar component
// Otherwise it can break Toasts showing logic
// We rely on information about the sidebar being mounted and collapsed
export const useCollapsibleMainSidebar = () => {
  const collapsed = useMainSidebarStore((s) => s.isCollapsed || !s.isMounted);
  const setCollapsed = useMainSidebarStore((s) => s.actions.setCollapsed);
  const setIsMounted = useMainSidebarStore((s) => s.actions.setIsMounted);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  return { collapsed, setCollapsed };
};

// It is expanded when the sidebar mounted and not collapsed
// Otherwise it is collapsed
export const useIsMainSidebarExpanded = () => {
  return useMainSidebarStore((s) => s.isMounted && !s.isCollapsed);
};
